import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Download, Mail, Save } from 'lucide-react';
import { Button } from '../components/Button';
import { Chip } from '../components/Chip';
import { Autocomplete } from '../components/Autocomplete';
import { budgets, durations, interests, localInterests, parties, travelStyles, tripTypes } from '../lib/constants';
import { createReport, emailReport, homeRecommendations } from '../lib/api';
import type { JourneyAnswers, ReportResult, SearchResult } from '../lib/types';

declare global {
  interface Window {
    turnstile?: { render: (el: string | HTMLElement, opts: Record<string, unknown>) => string; reset?: (id?: string) => void };
  }
}

const initialAnswers: JourneyAnswers = {
  originCity: 'Atlanta',
  destinationCity: '',
  tripType: '',
  duration: '',
  party: [],
  travelStyle: [],
  exploredHomePlaces: [],
  interests: [],
  localInterests: [],
  budget: '',
  notes: ''
};

function toggle(list: string[], item: string) {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export function Planner({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<JourneyAnswers>(() => {
    const saved = localStorage.getItem('uncle-honest-draft');
    return saved ? { ...initialAnswers, ...JSON.parse(saved) } : initialAnswers;
  });
  const [homePlaces, setHomePlaces] = useState<SearchResult[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<ReportResult | null>(null);
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [profileState, setProfileState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileRendered, setTurnstileRendered] = useState(false);

  useEffect(() => {
    localStorage.setItem('uncle-honest-draft', JSON.stringify(answers));
  }, [answers]);

  useEffect(() => {
    fetch('/api/config')
      .then((res): Promise<{ turnstileSiteKey?: string } | null> => res.ok ? res.json() : Promise.resolve(null))
      .then((data) => setTurnstileSiteKey(data?.turnstileSiteKey || null))
      .catch(() => setTurnstileSiteKey(null));
  }, []);

  useEffect(() => {
    if (step !== 11 || !turnstileSiteKey || turnstileRendered) return;
    const scriptId = 'cf-turnstile-script';
    const render = () => {
      const target = document.getElementById('turnstile-container');
      if (!target || !window.turnstile || turnstileRendered) return;
      window.turnstile.render(target, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setAnswers((a) => ({ ...a, turnstileToken: token }))
      });
      setTurnstileRendered(true);
    };
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.body.appendChild(script);
    } else {
      render();
    }
  }, [step, turnstileSiteKey, turnstileRendered]);

  useEffect(() => {
    if (step !== 6 || !answers.originCity) return;
    setLoadingPlaces(true);
    homeRecommendations(answers.originCity, answers.travelStyle)
      .then(setHomePlaces)
      .catch(() => setHomePlaces([]))
      .finally(() => setLoadingPlaces(false));
  }, [step, answers.originCity, answers.travelStyle]);

  const steps = useMemo(() => [
    'Starting point','Destination','Trip type','Duration','Who\'s coming','Travel style','Before you leave','Interests','Local interests','Budget','Anything else','Build report'
  ], []);

  const progress = ((step + 1) / steps.length) * 100;

  function canContinue() {
    if (step === 0) return Boolean(answers.originCity);
    if (step === 1) return Boolean(answers.destinationCity);
    if (step === 2) return Boolean(answers.tripType);
    if (step === 3) return Boolean(answers.duration);
    if (step === 4) return answers.party.length > 0;
    if (step === 5) return answers.travelStyle.length > 0;
    if (step === 9) return Boolean(answers.budget);
    return true;
  }

  async function buildReport() {
    setError('');
    setGenerating(true);
    try {
      const built = await createReport({ ...answers, email: email || undefined });
      setReport(built);
      setStep(12);
      localStorage.removeItem('uncle-honest-draft');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setGenerating(false);
    }
  }

  async function sendEmail() {
    if (!report || !email) return;
    setEmailState('sending');
    try {
      await emailReport(report.id, report.downloadKey, email);
      setEmailState('sent');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Email failed.');
      setEmailState('idle');
    }
  }

  async function saveProfile() {
    if (!report || !email) return;
    setProfileState('saving');
    try {
      const res = await fetch('/api/profile/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reportId: report.id, downloadKey: report.downloadKey })
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data?.error || 'Profile save failed.');
      setProfileState('saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Profile save failed.');
      setProfileState('idle');
    }
  }

  if (report) {
    return (
      <main className="mx-auto max-w-6xl px-5 py-10 md:px-8">
        <button onClick={onBack} className="focus-ring mb-8 inline-flex items-center rounded-full text-sm text-charcoal/60 hover:text-charcoal"><ArrowLeft className="mr-2 h-4 w-4" /> home</button>
        <section className="rounded-[2.5rem] border border-charcoal/10 bg-warm p-6 shadow-soft md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/45">your report is ready</p>
          <h1 className="mt-4 font-serif text-6xl leading-none tracking-[-0.065em] md:text-8xl">{report.title}</h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-charcoal/68">{report.subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a className="focus-ring inline-flex items-center rounded-full bg-charcoal px-6 py-3 text-sm font-semibold text-warm hover:bg-cocoa" href={report.pdfUrl} target="_blank" rel="noreferrer"><Download className="mr-2 h-4 w-4" /> Download PDF</a>
            <Button variant="secondary" onClick={sendEmail} disabled={!email || emailState === 'sending'}><Mail className="mr-2 h-4 w-4" /> {emailState === 'sent' ? 'Sent' : 'Email it'}</Button>
            <Button variant="secondary" onClick={saveProfile} disabled={!email || profileState === 'saving'}><Save className="mr-2 h-4 w-4" /> {profileState === 'saved' ? 'Profile saved' : 'Save profile'}</Button>
          </div>
          <div className="mt-5 max-w-md">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email address" className="focus-ring w-full rounded-full border border-charcoal/10 bg-bone px-5 py-3 text-sm" />
          </div>
          {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
        </section>
        <article className="prose-report mt-12 rounded-[2.5rem] border border-charcoal/10 bg-bone p-6 md:p-10">
          {report.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
              {section.items && section.items.length > 0 && (
                <ul className="mt-4 grid gap-2 md:grid-cols-2">
                  {section.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </section>
          ))}
        </article>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-5 py-8 md:px-8">
      <button onClick={step === 0 ? onBack : () => setStep((s) => s - 1)} className="focus-ring mb-8 inline-flex items-center rounded-full text-sm text-charcoal/60 hover:text-charcoal"><ArrowLeft className="mr-2 h-4 w-4" /> {step === 0 ? 'home' : 'back'}</button>
      <div className="mb-8 h-1 overflow-hidden rounded-full bg-charcoal/10"><div className="h-full bg-charcoal transition-all" style={{ width: `${progress}%` }} /></div>
      <section className="slide-up rounded-[2.5rem] border border-charcoal/10 bg-warm p-6 shadow-soft md:p-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/45">{step + 1} / {steps.length} · {steps[step]}</p>

        {step === 0 && <div className="mt-8 grid gap-5"><h1 className="font-serif text-6xl tracking-[-0.06em]">Where are you starting?</h1><Autocomplete label="city" placeholder="Atlanta" value={answers.originCity} onChange={(v, item) => setAnswers({ ...answers, originCity: v, originCountry: item?.country })} /><input className="focus-ring rounded-3xl border border-charcoal/10 bg-bone px-5 py-4" placeholder="Neighborhood (optional)" value={answers.originNeighborhood || ''} onChange={(e) => setAnswers({ ...answers, originNeighborhood: e.target.value })} /></div>}

        {step === 1 && <div className="mt-8 grid gap-5"><h1 className="font-serif text-6xl tracking-[-0.06em]">Where are you going?</h1><Autocomplete label="destination" placeholder="Lagos, Accra, Kingston..." value={answers.destinationCity} onChange={(v, item) => setAnswers({ ...answers, destinationCity: v, destinationCountry: item?.country })} /></div>}

        {step === 2 && <Choice title="What kind of journey is this?" items={tripTypes} selected={[answers.tripType]} onClick={(x) => setAnswers({ ...answers, tripType: x })} single />}
        {step === 3 && <Choice title="How long are you staying?" items={durations} selected={[answers.duration]} onClick={(x) => setAnswers({ ...answers, duration: x })} single />}
        {step === 4 && <Choice title="Who’s coming?" items={parties} selected={answers.party} onClick={(x) => setAnswers({ ...answers, party: toggle(answers.party, x) })} />}
        {step === 5 && <Choice title="What should UncleHonest pay attention to?" subtitle="Choose as many. This is your taste map." items={travelStyles} selected={answers.travelStyle} onClick={(x) => setAnswers({ ...answers, travelStyle: toggle(answers.travelStyle, x) })} />}

        {step === 6 && <div className="mt-8"><h1 className="font-serif text-6xl tracking-[-0.06em]">Before you leave...</h1><p className="mt-4 max-w-xl text-charcoal/65">Which {answers.originCity || 'home city'} places have you already explored? This helps us understand your taste without making you write an essay.</p>{loadingPlaces ? <p className="mt-8 text-sm text-charcoal/55">Loading local signals...</p> : <div className="mt-8 grid gap-3 md:grid-cols-2">{homePlaces.map((place) => <button key={place.id} type="button" onClick={() => setAnswers({ ...answers, exploredHomePlaces: toggle(answers.exploredHomePlaces, place.label) })} className={`focus-ring rounded-3xl border p-5 text-left transition ${answers.exploredHomePlaces.includes(place.label) ? 'border-charcoal bg-charcoal text-warm' : 'border-charcoal/10 bg-bone hover:border-charcoal/25'}`}><span className="block font-semibold">{place.label}</span><span className="mt-1 block text-sm opacity-65">{place.sublabel}</span></button>)}</div>}</div>}

        {step === 7 && <Choice title="Interests" subtitle="Cuisine, music, languages, communities, movement, learning — choose freely." items={interests} selected={answers.interests} onClick={(x) => setAnswers({ ...answers, interests: toggle(answers.interests, x) })} />}
        {step === 8 && <Choice title="I’d love to discover..." items={localInterests} selected={answers.localInterests} onClick={(x) => setAnswers({ ...answers, localInterests: toggle(answers.localInterests, x) })} />}
        {step === 9 && <Choice title="Budget" items={budgets} selected={[answers.budget]} onClick={(x) => setAnswers({ ...answers, budget: x })} single />}

        {step === 10 && <div className="mt-8"><h1 className="font-serif text-6xl tracking-[-0.06em]">Anything else?</h1><textarea value={answers.notes} onChange={(e) => setAnswers({ ...answers, notes: e.target.value })} placeholder="food restrictions, family needs, business goals, neighborhoods you’ve heard about, safety concerns..." className="focus-ring mt-8 min-h-48 w-full rounded-[2rem] border border-charcoal/10 bg-bone p-5 text-lg" /></div>}

        {step === 11 && <div className="mt-8"><h1 className="font-serif text-6xl tracking-[-0.06em]">Build your report.</h1><p className="mt-4 max-w-xl text-charcoal/65">We’ll blend your answers with curated corridor data and generate a PDF immediately.</p><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email address optional" className="focus-ring mt-8 w-full max-w-md rounded-full border border-charcoal/10 bg-bone px-5 py-3" />{turnstileSiteKey && <div id="turnstile-container" className="mt-5" />}{error && <p className="mt-4 text-sm text-red-700">{error}</p>}</div>}

        <div className="mt-10 flex justify-end gap-3">
          {step < 11 ? <Button onClick={() => setStep((s) => s + 1)} disabled={!canContinue()}>Continue <ArrowRight className="ml-2 h-4 w-4" /></Button> : <Button onClick={buildReport} disabled={generating}>{generating ? 'Building...' : 'Generate report'}</Button>}
        </div>
      </section>
    </main>
  );
}

function Choice({ title, subtitle, items, selected, onClick }: { title: string; subtitle?: string; items: string[]; selected: string[]; onClick: (x: string) => void; single?: boolean }) {
  return (
    <div className="mt-8">
      <h1 className="font-serif text-6xl tracking-[-0.06em]">{title}</h1>
      {subtitle && <p className="mt-4 max-w-xl text-charcoal/65">{subtitle}</p>}
      <div className="mt-8 flex flex-wrap gap-2">
        {items.map((item) => <Chip key={item} label={item} selected={selected.includes(item)} onClick={() => onClick(item)} />)}
      </div>
    </div>
  );
}
